import React, { useEffect, useRef, useState, useCallback, useContext } from "react";
import { AuthContext } from "../App";
import "../styles/NotePreview.css";
import { useNoteAnalytics } from "../utils/useNoteAnalytics";

function NotePreview({
  course,
  semester,
  subject,
  fileName,
  onClose,
  isBlurred,
  isLoggedIn: isLoggedInProp,
}) {
  const { currentUser } = useContext(AuthContext);
  const isLoggedIn = isLoggedInProp ?? !!currentUser;
  const iframeRef = useRef(null);

  // ── GA4 engagement tracking is wired below, after fileExists is declared ──

  const [isPinned, setIsPinned] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const [fileExists, setFileExists] = useState(true); // NEW

  // ── GA4 engagement tracking (non-invasive — no UI changes) ─────────────
  const { trackSearch, trackZoom, trackShare, trackPin } = useNoteAnalytics({
    course,
    semester,
    subject,
    fileName,
    fileExists,
    iframeRef,
  });

  const [searchTerm, setSearchTerm] = useState(""); // NEW
  const [searchError, setSearchError] = useState(""); // NEW
  const [matchesCount, setMatchesCount] = useState(0);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const matchesRef = useRef([]); // Array of <mark> elements inside iframe

  const scrollTrackRef = useRef(null);
  const [thumbTop, setThumbTop] = useState(0);
  const isDragging = useRef(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isDraggingState, setIsDraggingState] = useState(false);

  const zoomRef = useRef(1);
  const initialDistanceRef = useRef(null);
  const isMobile = window.innerWidth <= 768;

  // -------------------------------
  // Check if file exists BEFORE loading iframe
  // -------------------------------
  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(fileName, { method: "HEAD" });
        setFileExists(res.ok);
      } catch (e) {
        setFileExists(false);
      }
    };
    check();
  }, [fileName]);

  // -------------------------------
  // Auto-Maximize on Mobile
  // -------------------------------
  useEffect(() => {
    const checkMobile = () => {
      if (window.innerWidth <= 768) {
        setIsMaximized(true);
      }
    };

    checkMobile(); // Check on mount
    window.addEventListener("resize", checkMobile); // Optional: check if user rotates phone
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const PAGE_HEIGHT = 600; // Standard A4-ish height in pixels

  const updatePageNumber = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      const root = doc.scrollingElement || doc.documentElement;

      const scale = zoomRef.current || 1;

      // Convert back to logical (unscaled) scroll position
      const logicalScrollTop = root.scrollTop / scale;

      const page = Math.floor(logicalScrollTop / PAGE_HEIGHT) + 1;
      setCurrentPage(page);
    } catch (e) {}
  }, []);

  const handleIframeScroll = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || isDragging.current) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      const root = doc.scrollingElement || doc.documentElement;
      const scrolled = root.scrollTop;
      const maxScroll = root.scrollHeight - root.clientHeight;

      if (maxScroll <= 0) return;

      const scrollPercent = scrolled / maxScroll;
      const trackHeight = scrollTrackRef.current.offsetHeight;
      const thumbHeight = 35; // Updated ball size

      setThumbTop(scrollPercent * (trackHeight - thumbHeight));
      updatePageNumber(); // Update page during natural scroll
    } catch (e) {}
  }, [updatePageNumber]);

  const handleMouseMove = (e) => {
    if (!isDragging.current || !scrollTrackRef.current) return;

    const track = scrollTrackRef.current.getBoundingClientRect();
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    const root = doc.scrollingElement || doc.documentElement;

    // Calculate position within track
    let newTop = e.clientY - track.top - 15; // 15 is half ball height to center cursor
    const maxTop = track.height - 30;

    // Constrain within bounds
    if (newTop < 0) newTop = 0;
    if (newTop > maxTop) newTop = maxTop;

    setThumbTop(newTop);

    // Scroll the iframe content
    const scrollPercent = newTop / maxTop;
    const scrollTarget =
      scrollPercent * (root.scrollHeight - root.clientHeight);
    root.scrollTop = scrollTarget;
    updateScrollPosition(e.clientY);
  };
  const handleDragEnd = () => {
    isDragging.current = false;
    setIsDraggingState(false);
    setShowTooltip(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);
    document.body.style.userSelect = "auto";
  };

  const handleMouseUp = handleDragEnd;
  const handleTouchEnd = handleDragEnd;

  const handleMouseDown = (e) => {
    handleDragStart();
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.userSelect = "none";
  };

  // Add these to your existing handlers
  const handleTouchStart = (e) => {
    handleDragStart();
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
  };

  // Attach scroll listener to Iframe content
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const attachScrollListener = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.addEventListener("scroll", handleIframeScroll);

        // Hide default scrollbar inside iframe for a cleaner look
        const style = doc.createElement("style");
        style.textContent = `
          ::-webkit-scrollbar { width: 0px; background: transparent; }
          scrollbar-width: none;
          -ms-overflow-style: none;
        `;
        doc.head.appendChild(style);
      } catch (e) {
        console.warn("Could not attach scroll listener to iframe");
      }
    };

    iframe.addEventListener("load", attachScrollListener);
    return () => iframe.removeEventListener("load", attachScrollListener);
  }, [handleIframeScroll]);

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault(); // Prevents the actual page from scrolling
    const touch = e.touches[0];

    // Reuse your existing mouse move logic, but pass the touch Y coordinate
    updateScrollPosition(touch.clientY);
  };

  const updateScrollPosition = (clientY) => {
    const track = scrollTrackRef.current.getBoundingClientRect();
    const iframe = iframeRef.current;
    if (!iframe || !track) return;

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    const root = doc.scrollingElement || doc.documentElement;

    let newTop = clientY - track.top - 17;
    const maxTop = track.height - 35;

    if (newTop < 0) newTop = 0;
    if (newTop > maxTop) newTop = maxTop;

    setThumbTop(newTop);

    const scrollPercent = newTop / maxTop;
    const scrollTarget =
      scrollPercent * (root.scrollHeight - root.clientHeight);
    root.scrollTop = scrollTarget;

    updatePageNumber(); // Update page during drag
  };

  const handleDragStart = () => {
    isDragging.current = true;
    setIsDraggingState(true);
    setShowTooltip(true);
  };

  // -------------------------------
  // Load pinned status
  // -------------------------------
  useEffect(() => {
    const pinnedNotes = JSON.parse(localStorage.getItem("pinnedNotes") || "[]");
    const exists = pinnedNotes.some((note) => note.fileName === fileName);
    setIsPinned(exists);
  }, [fileName]);

  // -------------------------------
  // Pin / Unpin
  // -------------------------------
  const handlePin = () => {
    const pinnedNotes = JSON.parse(localStorage.getItem("pinnedNotes") || "[]");

    if (isPinned) {
      const updated = pinnedNotes.filter((n) => n.fileName !== fileName);
      localStorage.setItem("pinnedNotes", JSON.stringify(updated));
      setIsPinned(false);
      trackPin(false); // GA4: note_unpin
    } else {
      pinnedNotes.push({
        course,
        semester,
        subject,
        fileName,
        pinnedAt: new Date().toISOString(),
      });
      localStorage.setItem("pinnedNotes", JSON.stringify(pinnedNotes));
      setIsPinned(true);
      trackPin(true); // GA4: note_pin
    }
  };

  // ------------------------------------------
  // Force iframe content to fill full width
  // ------------------------------------------
  useEffect(() => {
    if (!fileExists) return; // skip if file missing

    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        const style = doc.createElement("style");

        style.textContent = `
  body, html {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    
  }
  body {
    padding-left: 16px !important;
    padding-right: 16px !important;
    box-sizing: border-box !important;
    background: ${document.body.classList.contains("dark-theme") ? "#121212" : "#ffffff"} !important;
    color: ${document.body.classList.contains("dark-theme") ? "#f1f1f1" : "#000000"} !important;
    
  }
  body * {
    color: inherit !important;
  }
  * {
    max-width: 100% !important;
  }

  /* Tables fix */
  table {
    width: 100% !important;
    border-collapse: collapse !important;
  }

  table, th, td {
    border: 1px solid ${document.body.classList.contains("dark-theme") ? "#444" : "#ccc"} !important;
  }

  th {
    background: ${document.body.classList.contains("dark-theme") ? "#1f1f1f" : "#f5f5f5"} !important;
  }

  /* Links */
  a {
    color: ${document.body.classList.contains("dark-theme") ? "#4da6ff" : "#0033cc"} !important;
  }
    

  /* 🔒 ANTI COPY / ANTI SELECT */
  body, body * {
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
  }
    
@media (max-width: 768px) {

    body * {
        padding-left: 8px !important;
        padding-right: 8px !important;
        font-size: 3vw !important;
        background: ${document.body.classList.contains("dark-theme") ? "#121212" : "#ffffff"} !important;
        color: ${document.body.classList.contains("dark-theme") ? "#f1f1f1" : "#000000"} !important;
    
    }
    table {
  transform-origin: 0 0;

}
`;
        doc.head.appendChild(style);

        // 🔒 Disable Ctrl+C, Ctrl+S, Ctrl+P, and text selection
        const script = doc.createElement("script");
        script.textContent = `
  document.addEventListener('copy', (e) => e.preventDefault());
  document.addEventListener('cut', (e) => e.preventDefault());
  document.addEventListener('paste', (e) => e.preventDefault());
  document.addEventListener('contextmenu', (e) => e.preventDefault());
  document.addEventListener('selectstart', (e) => e.preventDefault());

  document.addEventListener('keydown', (e) => {
    if (
      (e.ctrlKey || e.metaKey) &&
      ['c', 'x', 's', 'p', 'u', 'a'].includes(e.key.toLowerCase())
    ) {
      e.preventDefault();
    }
  });
`;
        doc.body.appendChild(script);
      } catch (err) {
        console.error("Iframe styling failed:", err);
      }
    };

    iframe.addEventListener("load", handleLoad);

    return () => iframe.removeEventListener("load", handleLoad);
  }, [fileExists, fileName, document.body.classList.contains("dark-theme")]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const attachPinch = () => {
      if (window.innerWidth > 768) return; // 🚫 desktop

      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;

        doc.addEventListener("touchstart", handleTouchStartPinch, {
          passive: false,
        });
        doc.addEventListener("touchmove", handleTouchMovePinch, {
          passive: false,
        });
        doc.addEventListener("touchend", handleTouchEndPinch);
      } catch (e) {}
    };

    iframe.addEventListener("load", attachPinch);
    return () => iframe.removeEventListener("load", attachPinch);
  }, []);

  const clearHighlights = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let doc;
    try {
      doc = iframe.contentDocument || iframe.contentWindow.document;
    } catch {
      return;
    }
    if (!doc) return;

    const marks = doc.querySelectorAll('mark[data-tej-search="1"]');
    marks.forEach((mark) => {
      const parent = mark.parentNode;
      if (!parent) return;

      while (mark.firstChild) {
        parent.insertBefore(mark.firstChild, mark);
      }
      parent.removeChild(mark);
      parent.normalize();
    });

    matchesRef.current = [];
  };

  const handleSearch = () => {
    // STEP 1: Always clear old highlights FIRST
    clearHighlights();
    setMatchesCount(0);
    setCurrentMatchIndex(-1);
    setSearchError("");

    const term = searchTerm.trim();
    if (!term || term.length < 2) {
      setSearchError("Enter at least 2 characters to search.");
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe) {
      setSearchError("Viewer not ready.");
      return;
    }

    let doc;
    try {
      doc = iframe.contentDocument || iframe.contentWindow.document;
    } catch {
      setSearchError("Cannot search inside this document.");
      return;
    }
    if (!doc || !doc.body) {
      setSearchError("Cannot access document.");
      return;
    }

    // STEP 2: Search for NEW term
    const termLower = term.toLowerCase();
    const walker = doc.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_TEXT,
      null,
      false,
    );
    const ranges = [];
    let node;

    while ((node = walker.nextNode())) {
      const text = node.nodeValue || "";
      if (!text) continue;

      const textLower = text.toLowerCase();
      let start = 0;
      let index;

      while ((index = textLower.indexOf(termLower, start)) !== -1) {
        const range = doc.createRange();
        range.setStart(node, index);
        range.setEnd(node, index + term.length);
        ranges.push(range);
        start = index + term.length;
      }
    }

    if (ranges.length === 0) {
      setSearchError("No matches found.");
      matchesRef.current = [];
      return;
    }

    // STEP 3: Create NEW marks for NEW term
    const marks = [];
    for (let i = ranges.length - 1; i >= 0; i--) {
      try {
        const range = ranges[i];
        const mark = doc.createElement("mark");
        mark.setAttribute("data-tej-search", "1");
        mark.style.backgroundColor = "yellow";
        mark.style.padding = "0 1px";

        range.surroundContents(mark);
        marks.unshift(mark);
      } catch (e) {
        console.warn("Highlight error:", e);
      }
    }

    // STEP 4: Update refs with NEW results
    matchesRef.current = marks;
    setMatchesCount(marks.length);
    setCurrentMatchIndex(0);
    scrollToMatch(0);
    trackSearch(term.length, marks.length); // GA4: note_search (no keyword sent)
  };

  const scrollToMatch = (index) => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let doc;
    try {
      doc = iframe.contentDocument || iframe.contentWindow.document;
    } catch {
      return;
    }
    if (!doc) return;

    const matches = matchesRef.current;
    if (!matches || !matches[index]) return;

    const el = matches[index];

    // Remove previous "active" style
    matches.forEach((m) => {
      m.style.outline = "none";
      m.style.backgroundColor = "yellow";
    });

    // Highlight active one
    el.style.backgroundColor = "orange";
    el.style.outline = "2px solid orange";

    const rect = el.getBoundingClientRect();
    const container = doc.scrollingElement || doc.documentElement || doc.body;

    const offset = 100; // adjust so match isn't at very top edge
    const top = rect.top + container.scrollTop - offset;

    container.scrollTo({ top, behavior: "smooth" });
  };

  const handleNext = () => {
    if (matchesCount === 0) return;

    setCurrentMatchIndex((prev) => {
      const next = (prev + 1) % matchesCount;
      scrollToMatch(next);
      return next;
    });
  };

  const handlePrev = () => {
    if (matchesCount === 0) return;

    setCurrentMatchIndex((prev) => {
      const next = (prev - 1 + matchesCount) % matchesCount;
      scrollToMatch(next);
      return next;
    });
  };

  useEffect(() => {
    clearHighlights();
    setSearchTerm("");
    setMatchesCount(0);
    setCurrentMatchIndex(-1);
    setSearchError("");
  }, [fileName]);

  // Add this useEffect AFTER your existing useEffects, BEFORE return
  useEffect(() => {
    if (searchTerm.trim() && matchesRef.current.length === 0) {
      // Auto-search when typing if iframe ready
      const iframe = iframeRef.current;
      if (iframe && iframe.contentDocument) {
        handleSearch();
      }
    }
  }, [searchTerm]);

  useEffect(() => {
    const handler = (e) => e.preventDefault();
    const preview = document.getElementById("preview-area");

    preview?.addEventListener("contextmenu", handler);

    return () => preview?.removeEventListener("contextmenu", handler);
  }, []);

  const getDistance = (touches) => {
    const [t1, t2] = touches;
    return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  };
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    const root = doc.scrollingElement || doc.documentElement;

    let lastY = 0;

    const onTouchStart = (e) => {
      lastY = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      const currentY = e.touches[0].clientY;
      const delta = lastY - currentY;

      const SCROLL_SLOW_FACTOR = 0.02; // 🔽 lower = slower
      root.scrollTop += delta * SCROLL_SLOW_FACTOR;

      lastY = currentY;
      e.preventDefault(); // stop native fast scroll
    };

    root.addEventListener("touchstart", onTouchStart, { passive: false });
    root.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  const applyPinchZoom = (scale, centerX, centerY) => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      const root = doc.scrollingElement || doc.documentElement;
      const body = doc.body;

      const prevScale = zoomRef.current;

      // Pinch center relative to document
      const rect = body.getBoundingClientRect();
      const offsetX = centerX - rect.left;
      const offsetY = centerY - rect.top;

      // Adjust scroll so content stays under fingers
      root.scrollLeft += offsetX * (scale / prevScale - 1);
      root.scrollTop += offsetY * (scale / prevScale - 1);

      body.style.transformOrigin = "0 0";
      body.style.transform = `scale(${scale})`;
      body.style.width = `${100 / scale}%`;
    } catch (e) {}
  };
  const resetZoom = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      const root = doc.scrollingElement || doc.documentElement;
      const body = doc.body;

      // 1️⃣ Get viewport center in content coordinates (vertical)
      const prevScale = zoomRef.current || 1;
      const viewportCenterY = root.scrollTop + root.clientHeight / 2;
      const contentCenterY = viewportCenterY / prevScale;

      // 2️⃣ Reset zoom
      zoomRef.current = 1;
      trackZoom(1); // GA4: note_zoom
      body.style.transform = "scale(1)";
      body.style.transformOrigin = "0 0";
      body.style.width = "100%";

      // 3️⃣ Reset horizontal scroll to left (0)
      // 4️⃣ Restore vertical scroll to keep the same content centered
      requestAnimationFrame(() => {
        root.scrollLeft = 0; // aligns to left
        const newScrollTop = contentCenterY - root.clientHeight / 2;
        root.scrollTop = Math.max(0, newScrollTop); // clamp top
      });
    } catch (e) {
      console.warn("Zoom reset failed", e);
    }
  };

  const handleTouchStartPinch = (e) => {
    if (e.touches.length === 2) {
      initialDistanceRef.current = getDistance(e.touches);
    }
  };

  const handleTouchMovePinch = (e) => {
    if (e.touches.length !== 2 || !initialDistanceRef.current) return;

    e.preventDefault();

    const t1 = e.touches[0];
    const t2 = e.touches[1];

    const newDistance = getDistance(e.touches);
    let scale = zoomRef.current * (newDistance / initialDistanceRef.current);

    scale = Math.min(Math.max(scale, 0.8), 2);

    // Pinch center point
    const centerX = (t1.clientX + t2.clientX) / 2;
    const centerY = (t1.clientY + t2.clientY) / 2;

    applyPinchZoom(scale, centerX, centerY);

    zoomRef.current = scale;
    trackZoom(Math.round(scale * 100) / 100); // GA4: note_zoom
    initialDistanceRef.current = newDistance;
  };

  const handleTouchEndPinch = () => {
    initialDistanceRef.current = null;
  };

  // -------------------------------
  // Convert Subject to Slug
  // -------------------------------
  const toSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  // -------------------------------
  // Share Note (Clean Route URL)
  // -------------------------------
  const handleShare = async () => {
    try {
      trackShare(); // GA4: note_share
      const slug = toSlug(subject);

      // Auto-detect type: either "notes" or "pyqs"
      const type = window.location.pathname.includes("/pyqs")
        ? "pyqs"
        : "notes";

      let sharePath = "";

      if (course === "3YDC") {
        sharePath =
          type === "pyqs"
            ? `/pyqs/3YDC/OU/${semester}/${slug}`
            : `/notes/3YDC/OU/${semester}/${slug}`;
      } else {
        sharePath =
          type === "pyqs"
            ? `/pyqs/5YDC/${course}/${semester}/${slug}`
            : `/notes/5YDC/${course}/${semester}/${slug}`;
      }

      const shareUrl = window.location.origin + sharePath;

      if (navigator.share) {
        await navigator.share({
          title: `${subject} | TejCodex`,
          text: `Check out ${subject} ${type} on TejCodex`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert("Link copied! You can now paste and share it.");
      }
    } catch (error) {
      console.error("Share failed:", error);
    }
  };
  
  return (
    <div className={`note-preview-overlay ${isMaximized ? "maximized" : ""}`}>
      <div className="note-preview-modal">
        <div className="note-preview-header">
          <h2>
            {subject} ({course} - Sem {semester})
          </h2>

          <div className="preview-buttons">
            <button
              className={`pin-btn ${isPinned ? "active" : ""}`}
              onClick={handlePin}
            >
              {isPinned ? "📌" : "📍"}{" "}
              <span className="btn-text">{isPinned ? "Unpin" : "Pin"}</span>
            </button>

            <button
              className="max-btn hide-on-mobile"
              onClick={() => setIsMaximized(!isMaximized)}
            >
              {isMaximized ? "🗕" : "🗖"}
            </button>

            <button className="share-btn" onClick={handleShare}>
              ➦
            </button>
            <button className="close-btn" onClick={onClose}>
              ✖
            </button>
          </div>
        </div>

        {fileExists && (
          <div className="note-preview-search">
            <input
              type="text"
              placeholder="Search in note..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();

                  // Only search if input length is at least 2
                  if (searchTerm.trim().length >= 2) {
                    if (
                      matchesRef.current.length === 0 ||
                      searchTerm.trim() !== e.target.value.trim()
                    ) {
                      handleSearch();
                    } else {
                      handleNext();
                    }
                  } else {
                    setSearchError("Enter at least 2 characters to search.");
                  }
                }
              }}
            />

            <button onClick={handlePrev} disabled={matchesCount === 0}>
              Prev
            </button>
            <button onClick={handleNext} disabled={matchesCount === 0}>
              Next
            </button>
            {/* Desktop: match counter */}
            {!isMobile && (
              <span className="note-preview-count">
                {matchesCount > 0
                  ? `${currentMatchIndex + 1} / ${matchesCount}`
                  : "0 / 0"}
              </span>
            )}

            {/* Mobile: reset zoom button */}
            {isMobile && (
              <button
                className="reset-zoom-btn"
                onClick={resetZoom}
                title="Reset zoom"
              >
                🔍 Reset
              </button>
            )}
          </div>
        )}

        {searchError && <div className="note-search-error">{searchError}</div>}

        {/* Show message ONLY when file doesn't exist */}
        {!fileExists && (
          <div className="note-error-box">
            🚧 Developer is preparing the notes for <strong>{subject}</strong>.
            The Notes will be uploaded soon. Please check back later! 🙂
          </div>
        )}

        {/* Only render iframe if file exists */}
        {fileExists && (
          <div className="note-preview-wrapper">
            <iframe
              ref={iframeRef}
              src={fileName}
              className={`note-iframe ${isBlurred ? "blurred" : ""}`}
              title="Note Preview"
            />

            {isBlurred && !isLoggedIn && (
              <div className="blur-overlay">
                <h2>Preview Time Expired ⏳</h2>
                <p>
                  Please Login or Sign Up to continue reading this note without
                  interruption.
                </p>
                <button
                  className="auth-button"
                  onClick={() => (window.location.href = "/auth")}
                >
                  Login / Sign Up
                </button>
              </div>
            )}
          </div>
        )}
        {/* CUSTOM SCROLL BALL TRACK */}
        <div className="custom-scroll-track" ref={scrollTrackRef}>
          {/* TOOLTIP BOX */}
          {showTooltip && (
            <div
              className="scroll-tooltip"
              style={{ top: `${thumbTop - 40}px` }}
            >
              Page {currentPage}
            </div>
          )}
          <div
            className={`scroll-ball ${isDraggingState ? "dragging" : ""}`}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            style={{ transform: `translateY(${thumbTop}px)` }}
          >
            {currentPage}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotePreview;
